import { useRef, useState } from "react";
import { Upload, FileText, X, Sun, Euro, Gauge, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { analyzeContract } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import LoadingState from "@/components/LoadingState";

const CircleEuroIcon = () => <Euro className="h-4 w-4 text-blue-700" />;

// Validates Dutch postcode: 4 digits + 2 letters (e.g. "1011AB" or "1011 AB")
function isValidPostcode(pc) {
  return /^[0-9]{4}\s?[A-Za-z]{2}$/.test(pc.trim());
}

export default function UploadSection() {
  const navigate = useNavigate();
  const contractRef = useRef(null);
  const jaarRef = useRef(null);
  const [contractFile, setContractFile] = useState(null);
  const [jaarFile, setJaarFile] = useState(null);
  const [monthlyAmount, setMonthlyAmount] = useState("");
  const [yearlyConsumption, setYearlyConsumption] = useState("");
  const [hasSolar, setHasSolar] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onContractDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleContract(file);
  };

  const handleContract = (file) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Alleen PDF bestanden zijn toegestaan voor het contract.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("PDF is te groot (max 20MB).");
      return;
    }
    setContractFile(file);
  };

  const handleJaar = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Alleen PDF bestanden zijn toegestaan voor jaarafrekening.");
      return;
    }
    setJaarFile(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!contractFile) {
      toast.error("Upload eerst je energiecontract (PDF).");
      return;
    }
    if (!monthlyAmount || Number(monthlyAmount) <= 0) {
      toast.error("Vul je termijnbedrag in (per maand).");
      return;
    }
    if (postcode && !isValidPostcode(postcode)) {
      toast.error("Postcode formaat klopt niet (bijv. 1011AB).");
      return;
    }
    setSubmitting(true);
    try {
      const data = await analyzeContract({
        contractFile,
        jaarafrekeningFile: jaarFile,
        monthlyAmount: Number(monthlyAmount),
        yearlyConsumption: yearlyConsumption ? Number(yearlyConsumption) : null,
        hasSolar,
        postcode: postcode.trim().replace(/\s/g, "").toUpperCase() || null,
      });
      // Backend returns immediately with state='processing'.
      // Navigate to the report page — it will poll until analysis is ready.
      toast.success("Bestand ontvangen — we starten de analyse!");
      navigate(`/rapport/${data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis.";
      toast.error(`Upload mislukt: ${msg}`);
      setSubmitting(false);
    }
  };

  if (submitting) {
    return <LoadingState />;
  }

  return (
    <section
      id="upload"
      className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-24"
      data-testid="upload-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10 fade-up">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Stap 1 · Upload & analyse
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Geef ons je contract. Wij doen de rest.
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Vul de basisgegevens in. We analyseren het PDF-contract en je termijnbedrag voor een eerlijk rapport.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-slate-200 rounded-2xl shadow-[0_2px_24px_rgba(15,23,42,0.04)] overflow-hidden fade-up fade-up-delay-1"
          data-testid="upload-form"
        >
          <div className="p-6 sm:p-8 space-y-6">
            {/* Contract dropzone */}
            <div>
              <Label className="font-heading text-sm font-semibold text-slate-900 mb-2 block">
                Energiecontract (PDF) <span className="text-red-500">*</span>
              </Label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onContractDrop}
                onClick={() => contractRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 sm:p-12 text-center cursor-pointer transition-all
                  ${dragOver ? "dropzone-active" : "border-slate-300 hover:border-blue-400 bg-slate-50/60 hover:bg-blue-50/40"}`}
                data-testid="contract-dropzone"
              >
                <input
                  ref={contractRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleContract(e.target.files[0])}
                  data-testid="contract-file-input"
                />
                {contractFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="h-8 w-8 text-blue-700" />
                    <div className="text-left">
                      <div className="font-medium text-slate-900" data-testid="contract-filename">{contractFile.name}</div>
                      <div className="text-xs text-slate-500">{(contractFile.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setContractFile(null); }}
                      className="ml-2 text-slate-400 hover:text-red-600"
                      data-testid="contract-remove-btn"
                      aria-label="Verwijder contract"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mx-auto h-14 w-14 rounded-full bg-blue-100 grid place-items-center mb-3">
                      <Upload className="h-7 w-7 text-blue-700" />
                    </div>
                    <div className="font-heading font-semibold text-slate-900">Sleep PDF hierheen of klik om te kiezen</div>
                    <div className="text-sm text-slate-500 mt-1">Max 20MB · PDF van je energieleverancier</div>
                  </>
                )}
              </div>
            </div>

            {/* Optional jaarafrekening */}
            <div>
              <Label className="font-heading text-sm font-semibold text-slate-900 mb-2 block">
                Jaarafrekening (optioneel)
              </Label>
              <div
                onClick={() => jaarRef.current?.click()}
                className="border border-slate-200 rounded-xl px-4 py-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors flex items-center justify-between"
                data-testid="jaar-dropzone"
              >
                <input
                  ref={jaarRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleJaar(e.target.files[0])}
                  data-testid="jaar-file-input"
                />
                {jaarFile ? (
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-600" />
                    <span className="text-sm text-slate-700" data-testid="jaar-filename">{jaarFile.name}</span>
                  </div>
                ) : (
                  <span className="text-sm text-slate-500">+ PDF jaarafrekening toevoegen (voor accuratere besparing)</span>
                )}
                {jaarFile && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setJaarFile(null); }}
                    className="text-slate-400 hover:text-red-600"
                    aria-label="Verwijder jaarafrekening"
                    data-testid="jaar-remove-btn"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Form grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="monthly_amount" className="font-heading text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                  <CircleEuroIcon />
                  Termijnbedrag per maand <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                  <Input
                    id="monthly_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="245"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(e.target.value)}
                    className="pl-8 h-11"
                    data-testid="monthly-amount-input"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="yearly_consumption" className="font-heading text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                  <Gauge className="h-4 w-4 text-blue-700" />
                  Jaarverbruik elektriciteit (optioneel)
                </Label>
                <div className="relative">
                  <Input
                    id="yearly_consumption"
                    type="number"
                    min="0"
                    placeholder="3000"
                    value={yearlyConsumption}
                    onChange={(e) => setYearlyConsumption(e.target.value)}
                    className="pr-12 h-11"
                    data-testid="yearly-consumption-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">kWh</span>
                </div>
              </div>
            </div>

            {/* Postcode (helps with regional cost analysis + comparison deep-links) */}
            <div>
              <Label htmlFor="postcode" className="font-heading text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-blue-700" />
                Postcode <span className="text-slate-400 font-normal">(optioneel — verbetert analyse + vergelijking)</span>
              </Label>
              <Input
                id="postcode"
                type="text"
                placeholder="1011AB"
                maxLength={7}
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                className="h-11 uppercase"
                data-testid="postcode-input"
              />
              <p className="mt-1.5 text-xs text-slate-500">
                We gebruiken je postcode voor regionale netbeheerkosten en om de juiste tarieven bij vergelijkers op te halen. Geen straat/huisnummer nodig.
              </p>
            </div>

            {/* Solar switch */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-amber-100 grid place-items-center">
                  <Sun className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold text-slate-900">Zonnepanelen aanwezig?</div>
                  <div className="text-xs text-slate-500">We checken de terugleververgoeding extra streng</div>
                </div>
              </div>
              <Switch
                checked={hasSolar}
                onCheckedChange={setHasSolar}
                data-testid="solar-switch"
                aria-label="Zonnepanelen toggle"
              />
            </div>
          </div>

          <div className="px-6 sm:px-8 py-5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Door op analyseer te klikken ga je akkoord met onze
              {" "}<a href="#" className="text-blue-700 hover:underline" data-testid="terms-link">voorwaarden</a>.
            </div>
            <button
              type="submit"
              className="bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg px-6 py-3 shadow-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
              disabled={submitting}
              data-testid="analyze-submit-button"
            >
              Analyseer contract nu
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
